// Status renderers for the report summary's tables.
//
// The report wire model types purchase-order, variation and SOW statuses as
// bare `string`, not as the frontend enums: they are echoed verbatim from
// stored records, some of which predate the current vocabularies. Each
// renderer therefore checks membership before reaching for a badge and falls
// back to the raw value as plain text. Casting the string to the enum instead
// would produce a blank pill (or a crash on a Record lookup) the first time a
// legacy row surfaced, and would hide the drift rather than showing it.

import TrackingStatusBadge from "../tracking/shared/TrackingStatusBadge";
import type { SowStatus, VariationStatus } from "../../../types";
import type { PurchaseOrderStatus } from "../../../types/finance";

const PURCHASE_ORDER_STATUSES: readonly string[] = ["open", "closed"];
const VARIATION_STATUSES: readonly string[] = [
  "proposed",
  "approved",
  "rejected",
];
const SOW_STATUSES: readonly string[] = [
  "draft",
  "active",
  "completed",
  "cancelled",
];

const PURCHASE_ORDER_BADGE: Record<
  PurchaseOrderStatus,
  { className: string; label: string }
> = {
  open: { className: "badge-active", label: "Open" },
  closed: { className: "badge-closed", label: "Closed" },
};

function UnknownStatus({ status }: { status: string }) {
  return (
    <span className="text-body-sm text-slate-600 dark:text-slate-300">
      {status}
    </span>
  );
}

export function PurchaseOrderStatusBadge({ status }: { status: string }) {
  if (!PURCHASE_ORDER_STATUSES.includes(status)) {
    return <UnknownStatus status={status} />;
  }
  const { className, label } =
    PURCHASE_ORDER_BADGE[status as PurchaseOrderStatus];
  return <span className={className}>{label}</span>;
}

export function VariationStatusBadge({ status }: { status: string }) {
  if (!VARIATION_STATUSES.includes(status)) {
    return <UnknownStatus status={status} />;
  }
  return (
    <TrackingStatusBadge kind="variation" status={status as VariationStatus} />
  );
}

export function SowStatusBadge({ status }: { status: string }) {
  if (!SOW_STATUSES.includes(status)) {
    return <UnknownStatus status={status} />;
  }
  return <TrackingStatusBadge kind="sow" status={status as SowStatus} />;
}
